import React, { useState, useEffect } from 'react';
import { ordersAPI, loadsAPI, vehiclesAPI, driversAPI, depotsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Plus, Truck, MapPin, Package, ArrowRight, Calendar, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PlanningPage = () => {
  const [unplannedOrders, setUnplannedOrders] = useState([]);
  const [loads, setLoads] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateLoadOpen, setIsCreateLoadOpen] = useState(false);
  const [newLoad, setNewLoad] = useState({
    vehicle_id: '',
    driver_id: '',
    depot_id: '',
    scheduled_date: format(new Date(), "yyyy-MM-dd'T'HH:mm")
  });
  const [selectedOrders, setSelectedOrders] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, loadsRes, vehiclesRes, driversRes, depotsRes] = await Promise.all([
        ordersAPI.getUnplanned(),
        loadsAPI.getToday(),
        vehiclesAPI.list({ is_active: true }),
        driversAPI.list({ is_active: true }),
        depotsAPI.list()
      ]);

      setUnplannedOrders(ordersRes.data);
      setLoads(loadsRes.data);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
      setDepots(depotsRes.data);
    } catch (error) {
      toast.error('Failed to load planning data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLoad = async () => {
    if (!newLoad.vehicle_id || !newLoad.driver_id || !newLoad.depot_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const stops = selectedOrders.map((order, index) => ({
        order_id: order.id,
        customer_id: order.customer_id,
        site_id: order.site_id,
        sequence: index + 1
      }));

      await loadsAPI.create({
        ...newLoad,
        scheduled_date: new Date(newLoad.scheduled_date).toISOString(),
        stops
      });

      toast.success('Load created successfully');
      setIsCreateLoadOpen(false);
      setNewLoad({
        vehicle_id: '',
        driver_id: '',
        depot_id: '',
        scheduled_date: format(new Date(), "yyyy-MM-dd'T'HH:mm")
      });
      setSelectedOrders([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create load');
    }
  };

  const toggleOrderSelection = (order) => {
    setSelectedOrders(prev => {
      const exists = prev.find(o => o.id === order.id);
      if (exists) {
        return prev.filter(o => o.id !== order.id);
      }
      return [...prev, order];
    });
  };

  const handleDispatchLoad = async (loadId) => {
    try {
      await loadsAPI.dispatch(loadId);
      toast.success('Load dispatched');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch load');
    }
  };

  const getVehicleName = (id) => {
    const v = vehicles.find(v => v.id === id);
    return v ? v.registration : 'N/A';
  };

  const getDriverName = (id) => {
    const d = drivers.find(d => d.id === id);
    return d ? `Driver ${d.license_number}` : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="planning-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Planning Board</h1>
          <p className="text-muted-foreground">Build loads and assign orders to vehicles</p>
        </div>
        <Button
          className="bg-orange-500 hover:bg-orange-400 text-white font-bold"
          onClick={() => setIsCreateLoadOpen(true)}
          disabled={selectedOrders.length === 0}
          data-testid="create-load-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Load ({selectedOrders.length})
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Unplanned Orders */}
        <Card className="h-[calc(100vh-16rem)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Unplanned Orders ({unplannedOrders.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-22rem)]">
              <div className="p-4 space-y-2">
                {unplannedOrders.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No unplanned orders
                  </p>
                ) : (
                  unplannedOrders.map((order) => {
                    const isSelected = selectedOrders.find(o => o.id === order.id);
                    return (
                      <div
                        key={order.id}
                        onClick={() => toggleOrderSelection(order)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-border hover:border-orange-500/50'
                        }`}
                        data-testid={`order-card-${order.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-mono text-sm font-medium">{order.order_number}</p>
                            <p className="text-sm font-medium mt-1">{order.customer_name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {order.site_name} - {order.site_address?.split(',')[2]?.trim() || ''}
                            </p>
                          </div>
                          <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
                            {order.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>{order.lines?.reduce((sum, l) => sum + l.quantity, 0).toLocaleString()} L</span>
                          <span>£{order.grand_total?.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Today's Loads */}
        <Card className="h-[calc(100vh-16rem)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Today's Loads ({loads.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-22rem)]">
              <div className="p-4 space-y-3">
                {loads.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No loads scheduled for today
                  </p>
                ) : (
                  loads.map((load) => (
                    <div
                      key={load.id}
                      className="p-4 rounded-lg border hover:border-orange-500/50 transition-colors"
                      data-testid={`load-card-${load.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-mono text-sm font-medium">{load.load_number}</p>
                          <p className="text-sm mt-1">{load.vehicle_registration || getVehicleName(load.vehicle_id)}</p>
                          <p className="text-xs text-muted-foreground">{load.driver_name || getDriverName(load.driver_id)}</p>
                        </div>
                        <Badge
                          className={
                            load.status === 'dispatched' || load.status === 'en_route'
                              ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400'
                              : load.status === 'completed'
                              ? 'status-success'
                              : 'status-pending'
                          }
                        >
                          {load.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {load.stop_count || load.stops?.length || 0} stops
                        </span>
                        <span>{load.total_volume?.toLocaleString() || 0} L</span>
                      </div>

                      {load.status === 'draft' || load.status === 'planned' ? (
                        <Button
                          size="sm"
                          className="mt-3 w-full bg-orange-500 hover:bg-orange-400 text-white font-bold"
                          onClick={() => handleDispatchLoad(load.id)}
                        >
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Dispatch
                        </Button>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Create Load Dialog */}
      <Dialog open={isCreateLoadOpen} onOpenChange={setIsCreateLoadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Load</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Vehicle *</Label>
              <Select value={newLoad.vehicle_id} onValueChange={(val) => setNewLoad({ ...newLoad, vehicle_id: val })}>
                <SelectTrigger data-testid="load-vehicle-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.registration} ({v.total_capacity_litres?.toLocaleString()}L)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Driver *</Label>
              <Select value={newLoad.driver_id} onValueChange={(val) => setNewLoad({ ...newLoad, driver_id: val })}>
                <SelectTrigger data-testid="load-driver-select">
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.license_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Depot *</Label>
              <Select value={newLoad.depot_id} onValueChange={(val) => setNewLoad({ ...newLoad, depot_id: val })}>
                <SelectTrigger data-testid="load-depot-select">
                  <SelectValue placeholder="Select depot" />
                </SelectTrigger>
                <SelectContent>
                  {depots.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} - {d.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Scheduled Date/Time</Label>
              <input
                type="datetime-local"
                value={newLoad.scheduled_date}
                onChange={(e) => setNewLoad({ ...newLoad, scheduled_date: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="load-datetime"
              />
            </div>

            <div className="space-y-2">
              <Label>Selected Orders ({selectedOrders.length})</Label>
              <div className="p-3 bg-muted rounded-lg max-h-40 overflow-y-auto">
                {selectedOrders.map((order, idx) => (
                  <div key={order.id} className="flex items-center justify-between py-1 text-sm">
                    <span>{idx + 1}. {order.order_number} - {order.customer_name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => toggleOrderSelection(order)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleCreateLoad} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="save-load-btn">
              Create Load
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanningPage;
